import { cx } from "@emotion/css";
import { BookText, ChevronDown, ChevronUp, Trophy } from "lucide-react";
import { ConfettiExplosion } from "react-confetti-explosion";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";
import routing from "routing";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { Menu } from "src/components/Default/LekUI/Menu";
import {
	Modal,
	ModalFooter,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import { ExamAnswersChart } from "src/components/Modals/StatisticsModals/ExamInfoStats";
import type { ExamAnswersStats } from "src/components/Modals/StatisticsModals/examStatsTypes";
import { useFinishedExamActions } from "src/components/NewComponents/menuActions/FinishedExamActions";
import { useGetExam } from "src/hooks/useExam";
import { ExamResultType, ExamState, ExamTypes } from "src/types/enums";

interface SummaryExam {
	id: string;
	name: string;
	type: string;
	state: string;
	result: string;
	courseId?: string;
	stats: ExamAnswersStats & { progress: number };
}

interface ExamSummaryModalProps {
	examId: string;
	onClose: () => void;
}

export const ExamSummaryModal = ({
	examId,
	onClose,
}: ExamSummaryModalProps) => {
	const { data } = useGetExam(examId) as { data?: SummaryExam };

	if (!data) {
		return null;
	}

	return <ExamSummaryContent exam={data} onClose={onClose} />;
};

interface ExamSummaryContentProps {
	exam: SummaryExam;
	onClose: () => void;
}

const ExamSummaryContent = ({ exam, onClose }: ExamSummaryContentProps) => {
	const { t } = useTranslation();
	const { stats } = exam;
	const isPassed = exam.result === ExamResultType.PASSED;
	const isCourse = exam.type === ExamTypes.COURSE && !!exam.courseId;
	const timeOver =
		exam.state === ExamState.EXPIRED ? t("modals.statistics.timeOver") : "";

	const { actions } = useFinishedExamActions({
		row: {
			id: exam.id,
			name: exam.name,
			wrongAnswers: stats.wrongAnswers,
			allResolved: stats.progress === 100,
		},
		onWatch: onClose,
		visibility: { remove: false },
	});

	const primaryUrl = isCourse
		? generatePath(routing.paths.course, { id: exam.courseId })
		: "/";

	return (
		<Modal onClose={onClose}>
			{isPassed && (
				<div className="flex-center-row">
					<ConfettiExplosion zIndex={10000} duration={3000} />
				</div>
			)}
			<div className="flex flex-col gap-24" data-test-id="exam-results">
				<ModalHeader
					Icon={isPassed ? Trophy : BookText}
					title={
						isPassed
							? t("modals.statistics.successTitle", "Gratulacje!")
							: t("modals.statistics.failTitle", "Tym razem się nie udało")
					}
					description={`${timeOver}${
						isPassed
							? t("modals.statistics.successSubtitle")
							: t("modals.statistics.failSubtitle")
					}`}
					hasCloseButton
				/>
				<div className="container-inline">
					<ExamAnswersChart
						stats={stats}
						showSkipped
						centerContent={
							<span className="flex flex-col items-center">
								<span data-test-id="exam-score">{stats.score}%</span>
								<span className="size-12 text-dark-grey">
									{t("modals.statistics.scoreLabel", "Twój wynik")}
								</span>
							</span>
						}
						chartFooter={
							<span
								className={cx(
									"chip chip--status",
									isPassed ? "tint-success" : "tint-error",
								)}
							>
								{isPassed
									? t("modals.statistics.passed", "Zdany")
									: t("modals.statistics.failed", "Niezdany")}
							</span>
						}
					/>
				</div>
				<ModalFooter className="border-top-1 pt-20">
					<Menu
						anchorId="more-actions"
						closeAfterClick
						items={actions.filter((action) => action !== false)}
						menuPopperClassName="z-index-9999"
						renderTrigger={({ onClick, anchor }) => {
							const Chevron = anchor ? ChevronUp : ChevronDown;
							return (
								<TextPrimaryButton
									id="more-actions"
									onClick={onClick}
									aria-haspopup="menu"
									aria-expanded={!!anchor}
									endIcon={<Chevron className="icon-small" />}
									data-test-id="summary-more-button"
								>
									{t("commands.more", "Więcej")}
								</TextPrimaryButton>
							);
						}}
					/>
					<PrimaryButton
						component={Link}
						to={primaryUrl}
						onClick={onClose}
						data-test-id="summary-primary-button"
					>
						{isCourse
							? t("modals.statistics.goToCourse", "Przejdź do kursu")
							: t("modals.statistics.goToHome", "Powrót na stronę główną")}
					</PrimaryButton>
				</ModalFooter>
			</div>
		</Modal>
	);
};
